import { useState, useCallback } from 'react';
import { pw9213Api } from '../../../services/pw9213Api';

/** Composite key identifier: "empCode|year" */
type RecordKey = string;

function makeKey(empCode: string, year: string): RecordKey {
  return `${empCode}|${year}`;
}

function parseKey(key: RecordKey): [string, string] {
  const idx = key.indexOf('|');
  return [key.slice(0, idx), key.slice(idx + 1)];
}

export function usePW9213Navigation(
  onNavigate: (empCode: string, year: string) => Promise<void>,
) {
  const [allKeys, setAllKeys] = useState<RecordKey[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);

  /** Load the full list of record keys */
  const loadList = useCallback(async (): Promise<RecordKey[]> => {
    const res = await pw9213Api.list(1, 10000);
    const keys = (res.data || []).map(r => makeKey(r.f913_01, r.f913_02)).filter(k => k !== '|');
    setAllKeys(keys);
    return keys;
  }, []);

  /** Navigate to a specific index */
  const navTo = useCallback(async (idx: number, isEditing: boolean) => {
    if (idx < 0 || idx >= allKeys.length || isEditing) return;
    setCurrentIdx(idx);
    const [empCode, year] = parseKey(allKeys[idx]);
    await onNavigate(empCode, year);
  }, [allKeys, onNavigate]);

  return {
    allKeys, currentIdx,
    setAllKeys, setCurrentIdx,
    loadList, navTo,
  };
}
